import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import { useHistory } from 'react-router-dom';
import { QueryVal } from 'Helpers/Query';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useIdentityChallengeContext } from 'Context/IdentityChallengeContext';
import { useNavContext } from 'Context/NavContext';
import Sentry from 'Integrations/Sentry';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import useAbortAllButLatest from 'Hooks/useAbortAllButLatest';
import { followRelationship } from 'Helpers/Affordances';

const PageContentContext = React.createContext(undefined);

export const PAGE_LINK_QUERY_PARAM = 'pageSelfLink';

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const SET_PAGE_CONTENT = 'set-page-content';
const SET_CURRENT_PAGE_NUMBER = 'set-current-page-number';
const PAGE_CONTENT_LOADING_STARTED = 'page-content-loading-started';
const PAGE_CONTENT_LOADING_SUCCEEDED = 'page-content-loading-succeeded';
const PAGE_CONTENT_LOADING_FAILED = 'page-content-loading-failed';
const SET_LOADING_PREV_OR_NEXT_PAGE = 'set-loading-prev-or-next-page';


const getSearchParams = () => {
  return new URL(window.location.href).searchParams;
};

const buildLinkFromUrl = (url) => {
  return fr.link({ data: { url } });
};

const init = () => {
  return {
    isInitializing: true,
    pageContentCollection: null,
    pageContentResource: null,
    currentPageNumber: 1,
    pageContentCollectionSelfLink: QueryVal(PAGE_LINK_QUERY_PARAM),
    isPageContentLoading: false,
    pageContentLoadingError: null,
    loadingPrevOrNextPage: false,
  };
};

// Reducer
const reducer = (state, action) => {
  const pageContentResource = action.pageContentCollection?.resources()?.head();
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        pageContentLoadingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        pageContentLoadingError: null,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        pageContentLoadingError: action.pageContentLoadingError,
      };
    }
    case SET_PAGE_CONTENT: {
      return {
        ...state,
        pageContentCollection: action.pageContentCollection,
        pageContentResource,
        pageContentCollectionSelfLink: action.pageContentCollection.link('self').url(),
      };
    }
    case PAGE_CONTENT_LOADING_STARTED: {
      return {
        ...state,
        isPageContentLoading: true,
        pageContentLoadingError: null,
      };
    }
    case SET_CURRENT_PAGE_NUMBER: {
      return {
        ...state,
        currentPageNumber: action.currentPageNumber,
      };
    }
    case PAGE_CONTENT_LOADING_SUCCEEDED: {
      return {
        ...state,
        isPageContentLoading: false,
        pageContentLoadingError: null,
      };
    }
    case PAGE_CONTENT_LOADING_FAILED: {
      return {
        ...state,
        isPageContentLoading: false,
        pageContentLoadingError: action.pageContentLoadingError,
      };
    }
    case SET_LOADING_PREV_OR_NEXT_PAGE: {
      return {
        ...state,
        loadingPrevOrNextPage: action.loadingPrevOrNextPage,
      };
    }
    default: {
      throw new Error(`Unhandled PageContentContext action type: ${action.type}`);
    }
  }
};

const PageContentProvider = ({
  children,
}) => {
  const { newSignal, inProgress, complete } = useAbortAllButLatest();
  const history = useHistory();
  const pageSelfLinkParam = getSearchParams().get(PAGE_LINK_QUERY_PARAM);
  const { setPageCollectionType } = useNavContext();
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const { displayErrorMessage } = useSnackbarContext();
  const { handleInterceptResponse } = useIdentityChallengeContext();
  //  AVOID LIKE THE PLAGUE, DO NOT USE - if you really want this behavior, you can build it explicitly in your code
  const deprecatedDebouncePageLoads = useRef(false);
  const setDeprecatedDebouncePageLoads = useCallback((val) => {
    deprecatedDebouncePageLoads.current = val;
  }, []);


  const setPageContentCollection = (possiblyDicrsResponse) => {
    if (!possiblyDicrsResponse?.link('self')?.url()) {
      throw new Error('setPageContent Error: Self link not found. PageContent must be a facet-rest resource');
    }
    return handleInterceptResponse(possiblyDicrsResponse, (originalRequestResponse) => {
      dispatch({ type: SET_PAGE_CONTENT, pageContentCollection: originalRequestResponse });
      setPageCollectionType(originalRequestResponse.meta().type());
    },
    (err) => {
      displayErrorMessage(err, err.errors);
    });
  };

  const setCurrentPageNumber = (currentPageNumber) => {
    dispatch({ type: SET_CURRENT_PAGE_NUMBER, currentPageNumber });
  };

  const setLoadingPrevOrNextPage = (loading) => {
    dispatch({ type: SET_LOADING_PREV_OR_NEXT_PAGE, loadingPrevOrNextPage: loading });
  };

  const loadPageContentLink = async (pageLinkOrRelationship) => {
    if (!pageLinkOrRelationship) {
      // This will not be caught by the error boundary due to is being in an async fn
      throw new Error('loadPageContent Error: Link not present');
    }

    const isRelationship = 'link' in pageLinkOrRelationship;
    const url = isRelationship
      ? pageLinkOrRelationship.link('related').url()
      : pageLinkOrRelationship.url();
    const fetch = isRelationship
      ? (opts) => followRelationship(pageLinkOrRelationship, undefined, opts)
      : (opts) => pageLinkOrRelationship.get(opts);

    if (deprecatedDebouncePageLoads.current && inProgress()) {
      return;
    }
    const { signal } = newSignal();

    dispatch({ type: PAGE_CONTENT_LOADING_STARTED });

    try {
      const updatedColl = await fetch({ signal });
      if (!signal.aborted) {
        await setPageContentCollection(updatedColl);
        dispatch({ type: PAGE_CONTENT_LOADING_SUCCEEDED });
      }
    } catch (err) {
      if (!signal.aborted) {
        Sentry.createSentryErrorLog(err);
        // eslint-disable-next-line no-console
        console.error(`Error setting page context with link ${url} - resources: ${JSON.stringify(state.pageContentCollection?.export())}`, err);
        dispatch({ type: PAGE_CONTENT_LOADING_FAILED, pageContentLoadingError: err });
        displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to load');
      }
    }

    if (!signal.aborted) {
      complete(); // Forget about the signal so that if deprecatedDebouncePageLoads is enabled, it won't have to worry about this signal being in progress
    }
  };

  const refreshPageContentCollection = React.useCallback(async (collection) => {
    const pageColl = collection || state.pageContentCollection;
    return loadPageContentLink(pageColl.link('self'));
  }, [state]);

  const loadNextPageContentCollection = React.useCallback(async (collection) => {
    // Start Loading State
    setLoadingPrevOrNextPage(true);
    // Get page collection
    const pageCollection = collection || state.pageContentCollection;
    // Set page number
    setCurrentPageNumber(state.currentPageNumber + 1);
    // Load next link
    await loadPageContentLink(pageCollection?.link('next'));
    // End Loading State
    setLoadingPrevOrNextPage(false);
  }, [state]);

  const loadPreviousPageContentCollection = React.useCallback(async (collection) => {
    // Start Loading State
    setLoadingPrevOrNextPage(true);
    // Get page collection
    const pageCollection = collection || state.pageContentCollection;
    // Set page number
    setCurrentPageNumber(state.currentPageNumber - 1);
    // Load prev link
    await loadPageContentLink(pageCollection?.link('prev'));
    // End Loading State
    setLoadingPrevOrNextPage(false);
  }, [state]);

  const submitPageResource = React.useCallback(async (updatedPageContentResource) => {
    let resp = null;
    let err = null;

    const { pageContentResource } = state;

    dispatch({ type: PAGE_CONTENT_LOADING_STARTED });

    try {
      const res = updatedPageContentResource || pageContentResource;
      resp = await res.submit();

      dispatch({ type: PAGE_CONTENT_LOADING_SUCCEEDED });
    } catch (e) {
      err = e;
      displayErrorMessage(err, err.errors);
      dispatch({ type: PAGE_CONTENT_LOADING_FAILED, pageContentLoadingError: err });
    }

    return { response: resp, error: err };
  }, [state]);

  React.useEffect(() => {
    if (state.pageContentCollectionSelfLink) {
      const pageHash = window.location.hash.substring(1); // get anchor from url
      const params = getSearchParams(history);
      params.set(PAGE_LINK_QUERY_PARAM, state.pageContentCollectionSelfLink);

      history.push({ search: params.toString() });

      if (pageHash) {
        window.location.hash = pageHash; // manually set anchor back on url
      }
    }
  }, [state.pageContentCollectionSelfLink]);

  React.useEffect(() => {
    const link = fr.link({ data: { url: pageSelfLinkParam } });
    if (pageSelfLinkParam !== state.pageContentCollectionSelfLink && pageSelfLinkParam) {
      loadPageContentLink(link);
    }
  }, [pageSelfLinkParam]);

  useComponentDidMount(async () => {
    dispatch({ type: INITIALIZE_STARTED });
    if (!pageSelfLinkParam) {
      throw new Error(`loadInitialPageLink Error: ${PAGE_LINK_QUERY_PARAM} not in search params`);
    }
    const pageLink = buildLinkFromUrl(pageSelfLinkParam);
    if (!pageLink) {
      throw new Error('loadPageContent Error: Link not present');
    }

    await loadPageContentLink(pageLink);
    dispatch({ type: INITIALIZE_SUCCEEDED });
  });

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      pageContentCollection: state.pageContentCollection,
      pageContentResource: state.pageContentResource,
      isPageContentLoading: state.isPageContentLoading,
      pageContentLoadingError: state.pageContentLoadingError,
      currentPageNumber: state.currentPageNumber,
      loadingPrevOrNextPage: state.loadingPrevOrNextPage,
      refreshPageContentCollection,
      loadNextPageContentCollection,
      loadPreviousPageContentCollection,
      hasNextPage: !!state.pageContentCollection?.link('next'),
      hasPrevPage: !!state.pageContentCollection?.link('prev'),
      setPageContentCollection,
      loadPageContentLink,
      submitPageResource,
      pageSelfLinkParam,
      setDeprecatedDebouncePageLoads, // AVOID LIKE THE PLAGUE, DO NOT USE - if you really want this behavior, you can build it explicitly in your code
    };
  }, [
    state,
    refreshPageContentCollection,
    loadNextPageContentCollection,
    loadPreviousPageContentCollection,
    setPageContentCollection,
    loadPageContentLink,
    submitPageResource,
    pageSelfLinkParam,
  ]);

  return (
    <PageContentContext.Provider value={memoizedValue}>
      {children}
    </PageContentContext.Provider>
  );
};

// Use PageContent State Context
const usePageContentContext = () => {
  const context = React.useContext(PageContentContext);
  if (context === undefined) {
    throw new Error('usePageContentContext must be used within a PageContentProvider');
  }
  return context;
};

// You probably want usePageContentContext instead - this is only useful if you're sure you're ok with not having page context loaded
export const useMaybePageContentContext = () => {
  return React.useContext(PageContentContext);
};

PageContentProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  PageContentProvider,
  usePageContentContext,
};
